/**
 *
 * BlockModal
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { omit } from 'lodash-es';
import { ButtonToolbar, Alert, Button, Modal } from 'react-bootstrap';
import PrimaryAction from 'components/interaction/PrimaryAction/index.jsx';
import DeleteButton from 'components/interaction/DeleteButton/index.jsx';
import AlertList from 'components/AlertList/index.jsx';
import Spinner from 'components/Spinner/index.jsx';
import IconButton from 'components/interaction/IconButton/index.jsx';
import PosAbsoluteFull from 'components/layout/PosAbsoluteFull/index.js';
import { idValues } from 'utils/arrays.js';
import { isEqual, isFuture, addMinutes, subMinutes } from 'utils/date-fns/index.js';
import { makeDateTimeString } from 'utils/eventTimes.js';
import { SLOT_ASSIGNED, SLOT_OPEN } from '../../../constants.js';
import TimeSlot from './TimeSlot.jsx';
import messages from './messages.js';
import DayModal from '../DayModal.jsx';
import useChangeSlot from '../useChangeSlot.js';
import useSaveTimeslots from '../../../hooks/useSaveTimeslots.js';
import useDeleteMeetings from '../../../hooks/useDeleteMeetings.js';

const ModalBody = styled(Modal.Body)`
  max-height: calc(100vh - 180px);
  overflow-y: auto;
`;

const FullPos = styled(PosAbsoluteFull)`
  background: rgba(255,255,255,0.5);
`;

const SlotCount = styled.span`
  font-size: 14px;
  font-weight: normal;
  margin-left: 1rem;
  margin-right: ${(p) => p.withCloseButton ? '1rem' : '0'};
`;

function BlockModal(props) {
  const { meetingSet, onHide, student } = props;
  // eslint-disable-next-line react/destructuring-assignment
  const { block, onSlotChange } = useChangeSlot(meetingSet, props.block);
  const { timeslots, availableSlots } = block;
  const intl = useIntl();
  const { duration, timeStep } = meetingSet;
  const { saveTimeslots } = useSaveTimeslots();
  const { deleteBlock } = useDeleteMeetings();
  const [working, setWorking] = useState(false);
  const [show, setShow] = useState(false);

  function getMergedTimeslots() {
    if (timeslots[0].blockId) {
      return timeslots;
    }
    const blocks = idValues(meetingSet.blocks);
    const blockBefore = blocks.find((b) => isEqual(b.end, timeslots[0].start));
    if (blockBefore) {
      return [...blockBefore.timeslots, ...timeslots.map((s) => ({ ...s, blockId: blockBefore.id }))];
    }
    const end = addMinutes(timeslots[timeslots.length - 1].end, meetingSet.inBetween);
    const blockAfter = blocks.find((b) => isEqual(b.start, end));
    if (blockAfter) {
      return [...timeslots.map((s) => ({ ...s, blockId: blockAfter.id })), ...blockAfter.timeslots];
    }
    return timeslots;
  }

  /**
   * Submit the block when allocating periods
   * @returns {*}
   */
  async function handleSubmit() {
    setWorking(true);
    await saveTimeslots(meetingSet.id, getMergedTimeslots())
      .catch(() => setWorking(false));
    onHide();
  }

  function handleDelete() {
    setWorking(true);
    deleteBlock(meetingSet.id, block.id, { error: { 410: messages.noCancel } })
      .then(onHide)
      .catch(setWorking(false));
  }

  async function handleCopy() {
    const copiedSlots = timeslots.map((slot) => ({
      ...omit(slot, ['id', 'blockId', 'meetingId']),
      status: slot.status === SLOT_ASSIGNED ? SLOT_OPEN : slot.status,
    }));
    setWorking(true);
    await saveTimeslots(meetingSet.id, copiedSlots, { success: messages.copyConfirm });
    setWorking(false);
  }

  function renderExtendSlot(idx) {
    const start = idx === 0 ? subMinutes(block.start, timeStep) : block.end;
    const event = {
      start,
      end: addMinutes(start, duration),
    };
    return <TimeSlot working={false} event={event} onChange={(type) => onSlotChange({ idx, type })} />;
  }

  const time = makeDateTimeString(block);
  const noCancel = timeslots.some((slot) => slot.status === SLOT_ASSIGNED);
  const title = noCancel ? intl.formatMessage(messages.noCancel) : '';
  const valid = timeslots.length > 0;
  const canCopy = valid && isFuture(block.start);
  const changeBlock = (data) => onSlotChange({ type: 'changeBlock', data });
  return (
    <Modal show animation={false} onHide={onHide}>
      <Modal.Header className="border-bottom-0">
        <Modal.Title className="d-flex justify-content-between align-items-baseline">
          <span>
            {student && <div>{`${meetingSet.title} - ${student.firstName}`}</div>}
            <FormattedMessage {...messages.header} values={{ id: block.pos, time }} />
            {!block.id && (
              <Button variant="link" onClick={() => setShow(true)}>
                <FormattedMessage {...messages.changeTime} />
              </Button>
            )}
          </span>
          <SlotCount><FormattedMessage {...messages.slotCount} values={{ count: availableSlots }} /></SlotCount>
        </Modal.Title>
      </Modal.Header>
      <ModalBody>
        <AlertList errorsOnly />
        {timeslots.length === 1
          && <Alert variant="warning"><FormattedMessage {...messages.singleSlot} /></Alert>}
        <div>
          {renderExtendSlot(0)}
          {timeslots.map((event, idx) => (
            <TimeSlot
              key={event.start}
              isForPeriod
              event={event}
              onChange={(type, data) => onSlotChange({ idx, type, data })}
              canRemove={timeslots.length > 1 && (idx === 0 || idx === timeslots.length - 1)}
            />
          ))}
          {renderExtendSlot(1)}
        </div>
      </ModalBody>
      <Modal.Footer className="position-relative">
        <PrimaryAction onCancel={onHide} onSubmit={handleSubmit} valid={valid}>
          <ButtonToolbar>
            {block.id && (
              <>
                <IconButton icon="ss-copy fs-3 lower1 text-white" className="me-3" variant="info" onClick={handleCopy} disabled={!canCopy} />
                <DeleteButton
                  variant="danger"
                  icon="ss-trash fs-3 lower-1"
                  disabled={noCancel}
                  title={title}
                  confirmText={messages.removeBlock}
                  onClick={handleDelete}
                />
              </>
            )}
          </ButtonToolbar>
        </PrimaryAction>
        {Boolean(working) && <FullPos><Spinner /></FullPos>}
      </Modal.Footer>
      {show && (
        <DayModal
          slotLength={timeStep}
          numSlots={timeslots.length}
          onHide={() => setShow(false)}
          onChange={changeBlock}
          value={block.start}
          timeOnly
        />
      )}
    </Modal>
  );
}

BlockModal.propTypes = {
  onHide: PropTypes.func,
  meetingSet: PropTypes.object,
  block: PropTypes.object,
  student: PropTypes.object,
};

export default BlockModal;
